import { c as _c } from "react/compiler-runtime";
import { EntityPlacementCtaTypeEnum } from '@cc-shopping/contracts/cross-site/marketplace/enums';
import { CTASearchParams } from '@cc-shopping/platform-analytics/lib/ctaSearchParams';
import { CdnImage, IMAGE_SIZES } from '@cc-shopping/ui/primitives/CdnImage';
import customImageWidthURLBuilder from '@cc-shopping/utils/custom-image-width-url-builder';
import { useApplyLinkForDirectLink } from '@cc-shopping/utils/directToApplication';
import { Box, type BoxProps, Typography } from '@mui/material';
import { forwardRef, type PropsWithChildren } from 'react';
import { CtaLink } from 'src/features/product-card/CTA/CtaLink';
import { isSxArray } from 'src/shared/utils/isSxArray';
import { type CompareAnalytics, useCreditCardCompareOfferContext } from '../../OfferContext';
import { CardAndTitleContainer } from './CardAndTitle.container';
import { RemoveCardButton } from './RemoveCardButton';
type ProductCardCardAndTitleProps = {
  additionalContainerProps?: BoxProps;
  variant?: 'default' | 'compact';
  analyticsProps?: Partial<CompareAnalytics>;
};
type ImageContainerProps = {
  additionalProps?: BoxProps & {
    'data-testid'?: string;
  };
  compact?: boolean;
};
export function ImageContainer(t0) {
  const $ = _c(13);
  const {
    additionalProps,
    compact: t1,
    children
  } = t0;
  const compact = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== additionalProps) {
    t2 = additionalProps ?? {};
    $[0] = additionalProps;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    sx
  } = t2;
  let t3;
  if ($[2] !== sx) {
    t3 = sx && isSxArray(sx) ? sx : sx ? [sx] : [];
    $[2] = sx;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const extendedSx = t3;
  const t4 = compact ? "80px" : "102px";
  let t5;
  if ($[4] !== t4) {
    t5 = {
      width: "100%",
      position: "relative",
      height: {
        xs: "80px",
        md: t4
      },
      mb: 2
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== extendedSx || $[7] !== t5) {
    t6 = [t5, ...extendedSx];
    $[6] = extendedSx;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== additionalProps || $[10] !== children || $[11] !== t6) {
    t7 = <Box {...additionalProps} sx={t6}>{children}</Box>;
    $[9] = additionalProps;
    $[10] = children;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}
export const ProductCardCardAndTitle = forwardRef<HTMLDivElement, ProductCardCardAndTitleProps>((t0, ref) => {
  const $ = _c(39);
  const {
    additionalContainerProps,
    analyticsProps,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const {
    analytics,
    offer
  } = useCreditCardCompareOfferContext();
  const {
    cta,
    displayName,
    id,
    imageOptions: t2
  } = offer;
  const {
    resizable,
    large
  } = t2;
  let t3;
  if ($[0] !== large.url || $[1] !== resizable) {
    t3 = customImageWidthURLBuilder(resizable, {
      customImageWidth: 300,
      customImageQuality: 100,
      imageParentWidth: 106
    }, large.url);
    $[0] = large.url;
    $[1] = resizable;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    optimized
  } = t3;
  const hideRemoveAndTitle = variant === "compact";
  const isReview = cta.type === EntityPlacementCtaTypeEnum.ReadReview;
  const isMonetizing = cta.type === EntityPlacementCtaTypeEnum.ApplyNow;
  let t4;
  if ($[3] !== id || $[4] !== isMonetizing || $[5] !== offer.cta.link) {
    t4 = {
      offerId: id,
      originalApplyLink: offer.cta.link,
      isMonetizing
    };
    $[3] = id;
    $[4] = isMonetizing;
    $[5] = offer.cta.link;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const modifiedCtaLink = useApplyLinkForDirectLink(t4);
  let t5;
  if ($[7] !== hideRemoveAndTitle) {
    t5 = !hideRemoveAndTitle && <RemoveCardButton />;
    $[7] = hideRemoveAndTitle;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== analytics || $[10] !== analyticsProps) {
    t6 = {
      ...analytics,
      ...analyticsProps
    };
    $[9] = analytics;
    $[10] = analyticsProps;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = cta.type === EntityPlacementCtaTypeEnum.ApplyNow ? "nofollow" : undefined;
  let t8;
  if ($[12] !== isReview) {
    t8 = isReview ? [] : [CTASearchParams.MONETIZING, CTASearchParams.SHOPPING_FINISH];
    $[12] = isReview;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      color: "black",
      width: "100%"
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = displayName || "product-image";
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      objectFit: "contain",
      objectPosition: "center"
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== optimized.image.url || $[17] !== t10) {
    t12 = <CdnImage src={optimized.image.url} alt={t10} fill={true} style={t11} sizes={IMAGE_SIZES.MINI_PRODUCT_CARD} />;
    $[16] = optimized.image.url;
    $[17] = t10;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== hideRemoveAndTitle || $[20] !== t12) {
    t13 = <ImageContainer compact={hideRemoveAndTitle}>{t12}</ImageContainer>;
    $[19] = hideRemoveAndTitle;
    $[20] = t12;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== displayName || $[23] !== hideRemoveAndTitle) {
    t14 = !hideRemoveAndTitle && <Typography sx={{
      textAlign: "center",
      fontWeight: 700,
      lineHeight: 1.5
    }}>{displayName}</Typography>;
    $[22] = displayName;
    $[23] = hideRemoveAndTitle;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== analytics.finishType || $[26] !== isReview || $[27] !== modifiedCtaLink || $[28] !== t13 || $[29] !== t14 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t15 = <CtaLink analytics={t6} type="PRODUCT_IMAGE" href={modifiedCtaLink} rel={t7} target="_blank" finishType={analytics.finishType} isReview={isReview} parameterActions={t8} sx={t9}>{t13}{t14}</CtaLink>;
    $[25] = analytics.finishType;
    $[26] = isReview;
    $[27] = modifiedCtaLink;
    $[28] = t13;
    $[29] = t14;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== additionalContainerProps || $[35] !== ref || $[36] !== t15 || $[37] !== t5) {
    t16 = <CardAndTitleContainer additionalProps={additionalContainerProps} innerRef={ref}>{t5}{t15}</CardAndTitleContainer>;
    $[34] = additionalContainerProps;
    $[35] = ref;
    $[36] = t15;
    $[37] = t5;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
});